/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * v2 filter row for list and board views: controls left to right, wrapping,
 * 8px apart, with an optional right-aligned result count and "Clear filters".
 *
 * @param {object} props
 * @param {React.ReactNode} props.children The controls.
 * @param {React.ReactNode} [props.count] Right-aligned count text, e.g. "24 shown".
 * @param {() => void} [props.onClear] Shows a "Clear filters" link when given and `canClear` is true.
 * @param {boolean} [props.canClear=true]
 * @param {string} [props.clearLabel="Clear filters"]
 * @param {object} [props.sx]
 */

import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import { v2 as t, V2_FONT_FAMILY } from "styles/tokens/v2";

export default function FilterBar({ children, count, onClear, canClear, clearLabel, sx }) {
  const showClear = Boolean(onClear) && canClear;
  const hasRight = showClear || (count !== null && count !== undefined && count !== "");
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1, fontFamily: V2_FONT_FAMILY, ...sx }}>
      {children}
      {hasRight && (
        <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1.5 }}>
          {showClear && (
            <ButtonBase
              type="button"
              onClick={onClear}
              sx={{
                fontFamily: "inherit",
                fontSize: 13,
                fontWeight: 600,
                color: t.link,
                borderRadius: "4px",
                "&:hover": { textDecoration: "underline" },
                "&.Mui-focusVisible": { outline: `2px solid ${t.link}`, outlineOffset: 2 },
              }}
            >
              {clearLabel}
            </ButtonBase>
          )}
          {count !== null && count !== undefined && count !== "" && (
            <Box component="span" sx={{ fontSize: 13, color: t.text3, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
              {count}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

FilterBar.propTypes = {
  children: PropTypes.node,
  count: PropTypes.node,
  onClear: PropTypes.func,
  canClear: PropTypes.bool,
  clearLabel: PropTypes.string,
  sx: PropTypes.object,
};

FilterBar.defaultProps = {
  children: null,
  count: null,
  onClear: undefined,
  canClear: true,
  clearLabel: "Clear filters",
  sx: {},
};
